import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePromotionsData } from './PromotionData';
import { motion, AnimatePresence } from 'framer-motion';
import CandyLoadingPage from '../snippets/games/CandyLoadingPage';
import JiliRushGame from '../snippets/games/JiliRushGame';
import LoginToPlay from './LoginToPlay';
import { useGlobalContext } from './GlobalContext';

const SPIN_DURATION = 2500;
const COOKIE_NAME = 'promoSlotClosed';

function getCookie(name) {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((item) => item.startsWith(`${name}=`));
    return cookie ? cookie.split('=')[1] : null;
}

function getNextMidnight() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
}

export default function PromoSlotModal({
    isOpen = true,
    onClose = () => {},
    promotions: customPromotions,
}) {
    const { promoSlotPromotions, isPromotionDataReady } = usePromotionsData();

    const promotions = customPromotions ?? promoSlotPromotions;
    const [showPopup, setShowPopup] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState(1);
    const [isSpinning, setIsSpinning] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const [leverActive, setLeverActive] = useState(false);
    const [loadedImages, setLoadedImages] = useState({});
    const [showJiliRush, setShowJiliRush] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [login, setLogin] = useState(false);

    const { isLoggedIn } = useGlobalContext();

    const timers = useRef([]);
    const locked = useRef(false);
    const total = promotions.length;

    const schedule = useCallback((fn, delay) => {
        const timer = setTimeout(fn, delay);
        timers.current.push(timer);
        return timer;
    }, []);

    const clearTimers = useCallback(() => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
    }, []);

    // Automatically show popup without date restrictions.
    useEffect(() => {
        if (!isOpen || !isPromotionDataReady || promotions.length === 0) {
            setShowPopup(false);
            return;
        }

        const isClosed = getCookie(COOKIE_NAME);

        setShowPopup(!isClosed);
    }, [isOpen, isPromotionDataReady, promotions.length]);

    // Preload promotion images.
    useEffect(() => {
        if (!showPopup) return;

        let cancelled = false;
        const images = [];

        for (const promo of promotions) {
            if (!promo?.image) continue;

            const image = new Image();
            images.push(image);

            const settle = () => {
                if (!cancelled) {
                    setLoadedImages((prev) =>
                        prev[promo.image]
                            ? prev
                            : {
                                  ...prev,
                                  [promo.image]: true,
                              }
                    );
                }
            };

            image.onload = settle;
            image.onerror = settle;
            image.src = promo.image;

            if (image.complete) settle();
        }

        return () => {
            cancelled = true;
            images.forEach((image) => {
                image.onload = null;
                image.onerror = null;
            });
        };
    }, [showPopup, promotions]);

    const getPromo = (offset) => {
        if (!total) return null;

        return promotions[(activeIndex + offset + total) % total];
    };

    const move = (step) => {
        if (locked.current || total <= 1) return;

        locked.current = true;
        setIsNavigating(true);
        setDirection(step);
        setLeverActive(false);

        requestAnimationFrame(() => setLeverActive(true));

        setActiveIndex((prev) => (prev + step + total) % total);

        schedule(() => setLeverActive(false), 650);

        schedule(() => {
            locked.current = false;
            setIsNavigating(false);
        }, 650);
    };

    const handleSpin = () => {
        if (locked.current || total <= 1) return;

        locked.current = true;
        setIsSpinning(true);
        setLeverActive(true);
        setDirection(1);

        schedule(() => setLeverActive(false), 650);

        const start = performance.now();

        const tick = () => {
            const progress = Math.min(
                (performance.now() - start) / SPIN_DURATION,
                1
            );

            if (progress >= 1) {
                setIsSpinning(false);
                locked.current = false;
                return;
            }

            setActiveIndex((prev) => (prev + 1) % total);

            schedule(tick, 65 + Math.pow(progress, 3) * 420);
        };

        tick();
    };

    // Close popup and remember until next midnight.
    const handleClose = () => {
        const expires = getNextMidnight();

        document.cookie = `${COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

        clearTimers();
        locked.current = false;

        setIsSpinning(false);
        setIsNavigating(false);
        setLeverActive(false);
        setShowPopup(false);

        onClose();
    };

    useEffect(() => {
        return () => clearTimers();
    }, [clearTimers]);

    useEffect(() => {
        if (!showPopup) {
            clearTimers();
            locked.current = false;
            return;
        }

        setActiveIndex(0);
        setIsSpinning(false);
        setIsNavigating(false);
        setLeverActive(false);

        return () => {
            clearTimers();
            locked.current = false;
        };
    }, [showPopup, clearTimers]);

    const visiblePromos = [getPromo(-1), getPromo(0), getPromo(1)];

    const isBusy = isSpinning || isNavigating;

    const variants = {
        enter: (dir) => ({
            x: dir > 0 ? '35%' : '-35%',
            opacity: 0,
            scale: 0.94,
            filter: 'blur(5px)',
        }),
        center: {
            x: '0%',
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
        },
        exit: (dir) => ({
            x: dir > 0 ? '-35%' : '35%',
            opacity: 0,
            scale: 0.94,
            filter: 'blur(5px)',
        }),
    };

    const handleOpenJiliRush = () => {
        setIsLoading(true);
        setShowJiliRush(true);
    };

    const handleLogin = () => {
        setLogin(true);
    };

    if (!isOpen || !showPopup) return null;

    return (
        <>
            {showJiliRush &&
                createPortal(
                    isLoading ? (
                        <CandyLoadingPage
                            onLoadingComplete={() => setIsLoading(false)}
                        />
                    ) : (
                        <JiliRushGame
                            onClose={() => {
                                setShowJiliRush(false);
                                setIsLoading(true);
                            }}
                        />
                    ),
                    document.body
                )}
            {!showJiliRush && (
                <div className="promo-slot-overlay">
                    <div
                        className="promo-slot-machine"
                        role="dialog"
                        aria-modal="true"
                        aria-label="FGH Promo Slot"
                    >
                        <picture className="promo-slot-frame-picture">
                            <source
                                media="(max-width: 768px)"
                                srcSet="/images/promo/PROMO-MACHINE-MOBILE.webp"
                            />
                            <img
                                className="promo-slot-frame"
                                src="/images/promo/PROMO-MACHINE.webp"
                                alt=""
                                draggable={false}
                            />
                        </picture>

                        <motion.button
                            type="button"
                            className="promo-slot-lever"
                            onClick={handleSpin}
                            disabled={isBusy || total <= 1}
                            aria-label="Pull lever to spin promotions"
                            initial={false}
                            animate={{
                                rotate: leverActive ? [0, 18, -8, 0] : 0,
                            }}
                            transition={{
                                duration: 0.65,
                                ease: 'easeInOut',
                            }}
                            style={{
                                transformOrigin: '50% 85%',
                                cursor: isBusy ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <img
                                src="/images/promo/LEVER.png"
                                alt=""
                                draggable={false}
                            />
                        </motion.button>

                        <button
                            type="button"
                            className="promo-slot-close"
                            onClick={handleClose}
                            aria-label="Close promo slot"
                        >
                            <img
                                src="/images/promo/PROMO-SLOT_BTN.webp"
                                alt=""
                            />
                        </button>

                        <div className="promo-slot-brand" />
                        <p className="promo-slot-subtitle" />

                        <div className="promo-slot-reels">
                            {visiblePromos.map((promo, index) => {
                                const ready = promo
                                    ? !!loadedImages[promo.image]
                                    : false;

                                return (
                                    <div
                                        className={`promo-slot-reel reel-${index}`}
                                        key={index}
                                        aria-busy={!ready}
                                    >
                                        <AnimatePresence
                                            initial={false}
                                            custom={direction}
                                            mode="wait"
                                        >
                                            {promo && (
                                                <motion.div
                                                    key={`${promo.id}-${activeIndex}`}
                                                    className="promo-slot-card"
                                                    custom={direction}
                                                    variants={variants}
                                                    initial="enter"
                                                    animate="center"
                                                    exit="exit"
                                                    transition={{
                                                        duration: isSpinning
                                                            ? 0.09
                                                            : 0.2,
                                                        ease: 'easeOut',
                                                    }}
                                                >
                                                    {!ready && (
                                                        <div
                                                            className="promo-slot-skeleton"
                                                            role="status"
                                                            aria-label="Loading promotion"
                                                        >
                                                            <div className="promo-slot-skeleton-glow" />
                                                            <div className="promo-slot-skeleton-heading" />
                                                            <div className="promo-slot-skeleton-art" />
                                                            <div className="promo-slot-skeleton-button" />
                                                        </div>
                                                    )}

                                                    <img
                                                        src={promo.image}
                                                        alt={promo.title}
                                                        draggable={false}
                                                        style={{
                                                            opacity: ready
                                                                ? 1
                                                                : 0,
                                                            transition:
                                                                'opacity 220ms ease',
                                                        }}
                                                        onLoad={() =>
                                                            setLoadedImages(
                                                                (prev) =>
                                                                    prev[
                                                                        promo
                                                                            .image
                                                                    ]
                                                                        ? prev
                                                                        : {
                                                                              ...prev,
                                                                              [promo.image]: true,
                                                                          }
                                                            )
                                                        }
                                                        onError={() =>
                                                            setLoadedImages(
                                                                (prev) =>
                                                                    prev[
                                                                        promo
                                                                            .image
                                                                    ]
                                                                        ? prev
                                                                        : {
                                                                              ...prev,
                                                                              [promo.image]: true,
                                                                          }
                                                            )
                                                        }
                                                    />

                                                    {ready && (
                                                        <button
                                                            type="button"
                                                            className="promo-slot-view"
                                                            disabled={isBusy}
                                                            onClick={() => {
                                                                if (isBusy)
                                                                    return;

                                                                if (
                                                                    promo.action ===
                                                                    'telegramApp'
                                                                ) {
                                                                    window.open(
                                                                        'https://t.me/fghofficialactivitygroup',
                                                                        '_blank',
                                                                        'noopener,noreferrer'
                                                                    );
                                                                    return;
                                                                }

                                                                if (
                                                                    promo.action ===
                                                                    'jiliRush'
                                                                ) {
                                                                    if (
                                                                        !isLoggedIn
                                                                    ) {
                                                                        handleLogin();
                                                                        return;
                                                                    }

                                                                    // User is logged in, open JILI Fortune Rush
                                                                    const expires =
                                                                        getNextMidnight();

                                                                    document.cookie = `${COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

                                                                    handleOpenJiliRush();
                                                                    return;
                                                                }

                                                                // Other promotions
                                                                if (promo.url) {
                                                                    const expires =
                                                                        getNextMidnight();

                                                                    document.cookie = `${COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

                                                                    window.location.assign(
                                                                        promo.url
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            {promo.action ===
                                                            'telegramApp'
                                                                ? 'JOIN NOW'
                                                                : 'VIEW PROMO'}
                                                        </button>
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            className="promo-slot-arrow promo-slot-prev"
                            onClick={() => move(-1)}
                            disabled={isBusy || total <= 1}
                            aria-label="Previous promotion"
                        >
                            <img src="/images/promo/PROMO-ARROW.webp" alt="" />
                        </button>

                        <button
                            type="button"
                            className="promo-slot-arrow promo-slot-next"
                            onClick={() => move(1)}
                            disabled={isBusy || total <= 1}
                            aria-label="Next promotion"
                        >
                            <img src="/images/promo/PROMO-ARROW.webp" alt="" />
                        </button>
                    </div>
                </div>
            )}
            {login && <LoginToPlay onClose={() => setLogin(false)} />}
        </>
    );
}
